import { type Dispatch, type SetStateAction, useContext } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { type TasksFilters } from 'components/hooks/tasks/useGetTasks'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Button } from 'slab'
import { useGetBrokersTasksJobs } from 'utils/web_api'
import { type BrokersBroker, type BrokersTaskJob } from 'utils/web_api.schemas'

import { BINDER_JOB_TYPE, DEPENDENT_CHANGE_JOB_TYPE, ENROLLMENT_JOB_TYPE, PAYMENT_JOB_TYPE } from './utils'

type TasksQuickFiltersProps = {
  filters: TasksFilters
  setFilters: Dispatch<SetStateAction<TasksFilters>>
}

// The below queries handle the different queues of tasks to be completed.
function setEnrollmentsQueue (
  setFilters: Dispatch<SetStateAction<TasksFilters>>,
  jobs: BrokersTaskJob[] | undefined
) {
  setFilters(() => ({
    handlers: jobs?.filter((job) => job.type === ENROLLMENT_JOB_TYPE).map((job) => job.id),
  }))
}

function setPaymentsQueue (
  setFilters: Dispatch<SetStateAction<TasksFilters>>,
  jobs: BrokersTaskJob[] | undefined
) {
  setFilters(() => ({
    handlers: jobs?.filter((job) => job.type === PAYMENT_JOB_TYPE).map((job) => job.id),
  }))
}

function setDependentsQueue (
  setFilters: Dispatch<SetStateAction<TasksFilters>>,
  jobs: BrokersTaskJob[] | undefined
) {
  setFilters(() => ({
    handlers: jobs?.filter((job) => job.type === DEPENDENT_CHANGE_JOB_TYPE).map((job) => job.id),
  }))
}

function setBindersQueue (
  setFilters: Dispatch<SetStateAction<TasksFilters>>,
  jobs: BrokersTaskJob[] | undefined
) {
  setFilters(() => ({
    handlers: jobs?.filter((job) => job.type === BINDER_JOB_TYPE).map((job) => job.id),
  }))
}

// These queries below are for enhancing the existing query.
// They do not overwrite the existing query, but rather add to it to augment the existing query for better triaging.
function setPending (
  setFilters: Dispatch<SetStateAction<TasksFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: ['pending'],
  }))
}

function setManual (
  setFilters: Dispatch<SetStateAction<TasksFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    execution_methods: ['manual'],
  }))
}

function setAutomated (
  setFilters: Dispatch<SetStateAction<TasksFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    execution_methods: ['automated'],
  }))
}

function setAssignedToMeFilters (
  setFilters: Dispatch<SetStateAction<TasksFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters((prev) => ({
    ...prev,
    ...(currentBroker ? { broker_ids: [currentBroker?.id] } : {}),
  }))
}

function setUnassignedFilters (
  setFilters: Dispatch<SetStateAction<TasksFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    broker_ids: ['unassigned'],
  }))
}

const TasksQuickFilters = ({
  setFilters,
}: TasksQuickFiltersProps) => {
  const { data: jobs, isLoading } = useGetBrokersTasksJobs()
  const { currentBroker } = useContext(BrokerContext)

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <Button
        variant='tertiary'
        onClick={() => setEnrollmentsQueue(setFilters, jobs)}
        disabled={isLoading}
        data-testid='enrollments-button'
      >
        <div className='flex items-center gap-2'>
          <div>Enrollments</div>
          {isLoading && <LoadingSpinnerCentered height='20px' width='20px' />}
        </div>
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setPaymentsQueue(setFilters, jobs)}
        disabled={isLoading}
        data-testid='payments-button'
      >
        <div className='flex items-center gap-2'>
          <div>Payments</div>
          {isLoading && <LoadingSpinnerCentered height='20px' width='20px' />}
        </div>
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setBindersQueue(setFilters, jobs)}
        disabled={isLoading}
        data-testid='binders-button'
      >
        <div className='flex items-center gap-2'>
          <div>Binders</div>
          {isLoading && <LoadingSpinnerCentered height='20px' width='20px' />}
        </div>
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setDependentsQueue(setFilters, jobs)}
        disabled={isLoading}
        data-testid='dependents-button'
      >
        <div className='flex items-center gap-2'>
          <div>Dependent Changes</div>
          {isLoading && <LoadingSpinnerCentered height='20px' width='20px' />}
        </div>
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setPending(setFilters)}
      >
        Pending
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setManual(setFilters)}
      >
        Manual
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setAutomated(setFilters)}
      >
        Automated
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setAssignedToMeFilters(setFilters, currentBroker)}
      >
        Assigned to me
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnassignedFilters(setFilters)}
      >
        Unassigned
      </Button>
    </div>
  )
}

export default TasksQuickFilters
